import { Box, Paper, Typography, IconButton, Stack } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { FieldArray, useField } from "formik";
import { useTranslation } from "react-i18next";
import Renderer from "./Renderer";
import {
  fieldKey,
  generateDefaultFromSchema,
  humanizeSettingKey,
} from "../utils";
import { resolveSettingIcon } from "../settingIcon";
import TextButton from "../../../components/Controls/TextButton";
import { Flex } from "../../../components/Container/Flex";
import { Colors } from "../../../utils/style/colors";

export default function ArrayInput({
  name,
  schema,
  groupName,
  onChange,
  locked,
}) {
  const fieldName = groupName ? `${groupName}.${fieldKey(name)}` : name;
  const [{ value: formValue }] = useField(fieldName);
  const { t } = useTranslation();

  const maxLength = schema.validations?.maxLength || Infinity;
  const minLength = schema.validations?.minLength;
  const itemSchema = schema.items;
  const TitleIcon = resolveSettingIcon(schema.icon);
  const hasMinLengthError = minLength ? formValue?.length < minLength : false;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        border: hasMinLengthError ? `1px solid ${Colors.danger}` : "none",
        borderRadius: 1,
      }}
    >
      {hasMinLengthError ? (
        <Typography variant="subtitle2" sx={{ color: Colors.danger }}>
          Minimum items required: {minLength || 0}
        </Typography>
      ) : null}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {TitleIcon && <TitleIcon color="action" size={0.9} />}
        <Typography variant="h6" sx={{ fontWeight: "medium" }}>
          {t(name, { defaultValue: humanizeSettingKey(name) })}
        </Typography>
      </Box>
      <Stack spacing={2}>
        <FieldArray
          name={fieldName}
          render={(arrayHelpers) => (
            <>
              {formValue?.map?.((item, idx) => (
                <Paper
                  key={idx}
                  sx={{
                    p: 1,
                  }}
                >
                  {!locked && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mb: 1,
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          arrayHelpers.remove(idx);
                          const updatedArray = [...formValue];
                          updatedArray.splice(idx, 1);
                          onChange?.({ [name]: updatedArray });
                        }}
                        size="medium"
                      >
                        <Typography>Delete</Typography>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Renderer
                      id={`${fieldName}[${idx}]`}
                      name={fieldName}
                      value={item}
                      schema={itemSchema}
                      groupName={groupName}
                      locked={locked}
                      onChange={(key, newValue) => {
                        // Extract the path relative to the current item
                        const itemPrefix = `${fieldName}[${idx}].`;
                        const relativePath = key.startsWith(itemPrefix)
                          ? key.substring(itemPrefix.length)
                          : key.split(".").slice(-1)[0];

                        // Split the relative path into parts
                        const pathParts = relativePath.split(".");

                        // Deep clone the current item
                        const updatedItem = JSON.parse(JSON.stringify(item));

                        // Navigate to the nested property and update it
                        let current = updatedItem;
                        for (let i = 0; i < pathParts.length - 1; i++) {
                          if (!current[pathParts[i]]) {
                            current[pathParts[i]] = {};
                          }
                          current = current[pathParts[i]];
                        }
                        current[pathParts[pathParts.length - 1]] = newValue;

                        const updatedArray = [...formValue];
                        updatedArray[idx] = updatedItem;

                        onChange?.({ [name]: updatedArray });
                      }}
                      displayTitle={false}
                      sxProps={{
                        p: 0,
                        border: 0,
                        bgcolor: Colors.white,
                        borderColor: Colors.white,
                        boxShadow: "none",
                      }}
                    />
                  </Box>
                </Paper>
              ))}
              {!locked &&
                (formValue?.length < maxLength ||
                  maxLength === Infinity ||
                  true) && (
                  <Flex justify="center" style={{ marginTop: 10 }}>
                    <TextButton
                      onClick={() => {
                        arrayHelpers.insert(
                          formValue?.length,
                          generateDefaultFromSchema(itemSchema)
                        );
                      }}
                      size="small"
                      startIcon={<AddIcon />}
                      style={{ alignSelf: "flex-start" }}
                    >
                      Add Item ({formValue?.length}/
                      {maxLength === Infinity ? "∞" : maxLength})
                    </TextButton>
                  </Flex>
                )}
            </>
          )}
        />
      </Stack>
    </Box>
  );
}
